import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Organization } from '../models/Organization';

/** Loads the top-level client account (`clients/{id}`) linked to a project. */
export function useLinkedClientAccount(clientAccountId: string | null | undefined) {
  const [clientAccount, setClientAccount] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(Boolean(clientAccountId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!clientAccountId) {
      setClientAccount(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, 'clients', clientAccountId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setClientAccount(null);
          setLoading(false);
          setError(null);
          return;
        }

        const data = snapshot.data();
        setClientAccount({
          id: snapshot.id,
          ...data,
          clientIds: data.clientIds || data.projectIds || [],
          projectIds: data.projectIds || data.clientIds || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Organization);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching linked client account:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [clientAccountId]);

  return { clientAccount, loading, error };
}
